import { aircraftTypeById, airspacesForRoute } from "../data/worldQueries";
import { validateAircraftCapabilities } from "./aircraft";
import { airspaceStatus, airportCapacityMultiplier } from "./events";
import type { Aircraft, Airport, DispatchAssignment, GlobalEvent } from "../types";


export interface DispatchBindingSnapshot {
  day: number;
  assignmentCount: number;
  usedAircraftIds: string[];
  aircraft: Array<Pick<Aircraft, "id" | "typeId" | "status" | "availableFromDay">>;
}

export interface DispatchBindingRequest {
  targetType: DispatchAssignment["targetType"];
  targetId: string;
  preferredTypeId?: string;
}

export interface OperationValidationRequest {
  day: number;
  plannedTypeId?: string;
  origin: Airport;
  destination: Airport;
  distanceKm: number;
  assignment: DispatchAssignment | undefined;
  licenseRequired: string | undefined;
  grantedLicenseCodes: string[];
  aircraft: Aircraft | undefined;
  events: GlobalEvent[];
}

export function buildAutoAssignment(snapshot: DispatchBindingSnapshot, request: DispatchBindingRequest): DispatchAssignment | null {
  const usedAircraft = new Set(snapshot.usedAircraftIds);
  const aircraftCandidates = snapshot.aircraft.filter((item) => isAircraftDispatchable(item, snapshot.day) && !usedAircraft.has(item.id));
  const aircraft = aircraftCandidates.find((item) => !request.preferredTypeId || item.typeId === request.preferredTypeId) ?? aircraftCandidates[0];
  if (!aircraft) {
    return null;
  }
  return {
    id: `assign-${String(snapshot.assignmentCount + snapshot.day + 1).padStart(4, "0")}`,
    targetType: request.targetType,
    targetId: request.targetId,
    aircraftId: aircraft.id,
    fuelPolicy: "minimum_cost",
    assignmentMode: "auto",
    updatedDay: snapshot.day
  };
}

export function validateOperation(request: OperationValidationRequest): string[] {
  const notes: string[] = [];
  if (!request.assignment) {
    return ["未完成飞机调度指派。"];
  }

  const aircraft = request.aircraft;
  if (!aircraft) {
    return ["指派飞机不存在。"];
  }
  const type = aircraftTypeById(aircraft.typeId);
  if (!type) {
    return ["机型数据缺失。"];
  }
  const plannedType = plannedAircraftType(request.plannedTypeId);
  if (request.plannedTypeId && aircraft.typeId !== request.plannedTypeId && type.family !== plannedType?.family) {
    notes.push(`机型不匹配，计划需要 ${request.plannedTypeId}。`);
  }
  notes.push(...validateAircraftCapabilities({ aircraft, type, origin: request.origin, destination: request.destination, distanceKm: request.distanceKm }));
  if (aircraft.status === "maintenance" || aircraft.status === "grounded" || aircraft.availableFromDay > request.day) {
    notes.push(`${aircraft.registration} 当前不可用。`);
  }
  if (aircraft.hoursSinceMaintenance >= type.maintenanceIntervalHours) {
    notes.push(`${aircraft.registration} 已超过例行检查小时。`);
  }
  if (request.licenseRequired && !request.grantedLicenseCodes.includes(request.licenseRequired)) {
    notes.push(`缺少经营许可：${request.licenseRequired}。`);
  }
  if (airportCapacityMultiplier(request.origin, request.events) < 0.35 || airportCapacityMultiplier(request.destination, request.events) < 0.35) {
    notes.push("起降机场容量受限，当前无法执行。");
  }
  const closedAirspace = airspacesForRoute(request.origin, request.destination).find((airspace) => {
    const status = airspaceStatus(airspace.id, request.origin, request.destination, request.events);
    return status === "closed";
  });
  if (closedAirspace) {
    notes.push(`领空不可用：${closedAirspace.nameZh} 已关闭。`);
  }
  return notes;
}

function plannedAircraftType(typeId: string | undefined) {
  if (!typeId) {
    return undefined;
  }
  return aircraftTypeById(typeId);
}

function isAircraftDispatchable(aircraft: Pick<Aircraft, "status" | "availableFromDay">, day: number): boolean {
  return aircraft.status === "available" && aircraft.availableFromDay <= day;
}
